import { FormControl, Grid, IconButton, TextField, Typography, useTheme } from '@mui/material';
import useAlert from '../../../hooks/useAlert';
import React, { useState } from 'react';
import { createUserFeedback as createUserFeedbackMutation } from 'vendor-marketplace-shared-library';

import { useMutation } from '@apollo/client';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import PrimaryButton from '../../../components/common/styled-mui-overrides/PrimaryButton';

export const USER_FEEDBACK_RATINGS = {
  GOOD: 'good',
  NEUTRAL: 'neutral',
  BAD: 'bad',
};

export default function FeedbackTab() {
  const alert = useAlert();
  const theme = useTheme();
  const [rating, setRating] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [errors, setErrors] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [createUserFeedback] = useMutation(createUserFeedbackMutation, {
    variables: {
      input: {
        content,
        rating,
      },
    },
    onError: () => {
      alert.setAlert({ message: 'Could not submit feedback. Please try again later.' });
    },
  });

  const validateFeedbackSubmission = () => {
    if (!rating) {
      setErrors(['rating']);
      return false;
    }
    if (!content) {
      setErrors(['content']);
      return false;
    }
    return true;
  };

  const onSubmit = async () => {
    if (validateFeedbackSubmission()) {
      await createUserFeedback();
      setSubmitted(true);
      setRating('');
      setContent('');
    }
  };

  const updateRating = (rating: string) => {
    setRating(rating);
    setErrors(errors.filter(e => e !== 'rating'));
  };

  const selectedIconButton = {
    backgroundColor: theme.palette.secondary.light,
    border: `1px ${theme.palette.secondary.light} solid`,
  };

  return (
    <Grid container paddingTop={2}>
      <Grid item xs={0} md={3}></Grid>
      <Grid item xs={12} md={6}>
        {submitted ? (
          <Grid container direction={'column'} sx={styles.center} spacing={2}>
            <Grid item>
              <Typography>Thank you for your submission!</Typography>
            </Grid>
            <Grid item>
              <PrimaryButton onClick={() => setSubmitted(false)}>
                Submit Additional Feedback
              </PrimaryButton>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2} direction={'column'} sx={styles.mainContainer}>
            <Grid item>
              <Typography>How are we doing?</Typography>
            </Grid>
            <Grid item container spacing={2} sx={styles.mainContainer}>
              <Grid item>
                <IconButton
                  sx={
                    rating === USER_FEEDBACK_RATINGS.GOOD ? selectedIconButton : styles.iconButton
                  }
                >
                  <SentimentSatisfiedAltIcon
                    color={'success'}
                    fontSize={'large'}
                    onClick={() => updateRating(USER_FEEDBACK_RATINGS.GOOD)}
                  />
                </IconButton>
              </Grid>
              <Grid item>
                <IconButton
                  sx={
                    rating === USER_FEEDBACK_RATINGS.NEUTRAL
                      ? selectedIconButton
                      : styles.iconButton
                  }
                >
                  <SentimentNeutralIcon
                    fontSize={'large'}
                    onClick={() => updateRating(USER_FEEDBACK_RATINGS.NEUTRAL)}
                  />
                </IconButton>
              </Grid>
              <Grid item>
                <IconButton
                  sx={rating === USER_FEEDBACK_RATINGS.BAD ? selectedIconButton : styles.iconButton}
                >
                  <SentimentVeryDissatisfiedIcon
                    color={'error'}
                    fontSize={'large'}
                    onClick={() => updateRating(USER_FEEDBACK_RATINGS.BAD)}
                  />
                </IconButton>
              </Grid>
            </Grid>
            <Grid item>
              <Typography>Describe your experience</Typography>
            </Grid>
            <Grid item sx={styles.partialFullWidth}>
              <FormControl fullWidth>
                <TextField
                  fullWidth
                  placeholder={"Ex: it's so easy to use!"}
                  variant='outlined'
                  value={content}
                  multiline
                  rows={4}
                  onChange={e => setContent(e.target.value)}
                />
              </FormControl>
            </Grid>
            <Grid item>
              <PrimaryButton onClick={onSubmit}>Submit feedback</PrimaryButton>
            </Grid>
          </Grid>
        )}
      </Grid>
      <Grid item xs={0} md={3}></Grid>
    </Grid>
  );
}

const styles = {
  mainContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconButton: {
    backgroundColor: 'white',
  },
  center: {
    alignItems: 'center',
  },
  partialFullWidth: {
    width: '80%',
  },
};
